import { AxiosPromise } from 'axios';
import { Result } from '../result';

/**
 * Parses a JavaScript object, which can take on any type, into an array of
 * error codes based on the typical schema returned by our back-end.
 */
const getApiErrorCodes = (error: unknown): number[] => {
  const errorCodes: number[] = [];
  if (!error || typeof error !== 'object') {
    return [];
  }

  const { errors } = error as Record<string, unknown>;
  if (!(errors instanceof Array)) {
    return [];
  }

  errors.forEach((errorObject: unknown) => {
    if (!errorObject || typeof errorObject !== 'object') {
      return;
    }

    const { code } = errorObject as Record<string, unknown>;
    if (typeof code === 'number') {
      errorCodes.push(code);
    }
  });

  return errorCodes;
};

/**
 * Gets a single error code based on a JS object thrown by Axios.
 */
const parseErrorCode = (error: unknown): number | null => {
  const errorCodes = getApiErrorCodes(error);
  if (typeof error === 'object') {
    // Sometimes the response returned by Axios hides the errors in `error.data`.
    getApiErrorCodes((error as Record<string, unknown>).data).forEach(item =>
      errorCodes.push(item)
    );
  }

  return errorCodes[0] || null;
};

/**
 * Converts an unknown value into an enum type.
 */
export const toEnum = <T>(enumType: T, value: unknown): T[keyof T] | null => {
  if (enumType == null) {
    return null;
  }

  if (Object.values(enumType).includes(value)) {
    return value as T[keyof T];
  }

  return null;
};

/**
 * Converts an Axios HTTP request into a `Result`.
 */
export const toResult = async <T, M>(
  request: AxiosPromise<T>,
  errorEnum: M
): Promise<Result<T, M[keyof M] | null>> => {
  try {
    const response = await request;
    return Result.ok(response.data);
  } catch (error) {
    const errorCode = parseErrorCode(error);
    return Result.error(toEnum(errorEnum, errorCode));
  }
};
